import { useRuntimeConfig } from '#imports';
import * as Sentry from '@sentry/nuxt';

// Errors thrown when a fetch is cancelled or fails at the network layer
// (user navigated away, transient connection drop, captive portal, ad-blocker,
// third-party widget hitting a host that's down, etc.). Browsers report this
// inconsistently — Safari uses a generic "Load failed", Chromium/Firefox use
// "Failed to fetch" or "NetworkError..." or AbortError. Sentry's fetch
// instrumentation may also append the host: "Failed to fetch (x)". And ofetch
// wraps the underlying failure in its own FetchError with the request prefixed:
// `[GET] "/api/...": <no response> ...`. None of these are actionable from the
// dashboard, so filter them out rather than chase per call site.
//
// Heuristic: the `<no response>` substring is ofetch's canonical marker for
// "the network never returned anything" — covers all wrapped variants.
const NETWORK_FAIL_PHRASES = [
  'Load failed', // Safari / iOS WebKit
  'Failed to fetch', // Chromium (with optional " (host)" suffix)
  'NetworkError when attempting to fetch resource', // Firefox
];

const isCancelledFetchError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  if (error.name === 'AbortError') return true;

  const msg = error.message;

  // Bare browser TypeError from window.fetch
  if (error instanceof TypeError) {
    if (NETWORK_FAIL_PHRASES.some(p => msg.startsWith(p))) return true;
    if (msg === 'cancelled' || msg === 'cancelled due to user navigation') return true; // Safari variants
  }

  // ofetch FetchError wraps the underlying failure: `[METHOD] "url": <no response> ...`
  if (error.name === 'FetchError' && msg.includes('<no response>')) return true;

  return false;
};

// Dynamic-import failures, both from our own Nuxt chunks during deploys and
// from third-party scripts (e.g. AutoUncle) that ship hashed Vite chunks and
// 404 old hashes after their deploys.
//
// For our own chunks, the chunk-reload plugin (~/plugins/chunk-reload.client.ts)
// auto-recovers via reload and emits an info-level event when loop protection
// trips, so the raw errors are pure noise.
//
// For third-party chunks, we have no recovery path, and the errors are not
// actionable from our side — silencing keeps the dashboard usable.
const isHandledChunkLoadFailure = (error: unknown): boolean => {
  const msg = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return (
    msg.startsWith('Importing a module script failed') || // Safari / WebKit
    msg.startsWith('Failed to fetch dynamically imported module') || // Chromium
    msg.startsWith('error loading dynamically imported module') // Firefox
  );
};

// Stack-overflow RangeErrors caught by the global onerror/onunhandledrejection
// handler with no app-code frames in the stack. Real recursions in our own code
// will have at least one frame from `app/` or `server/` and won't match this
// filter — only noisy third-party/inline-script crashes (GTM, Hotjar, Cookiebot
// etc.) get dropped.
const isThirdPartyStackOverflow = (error: unknown, event: Sentry.ErrorEvent): boolean => {
  if (!(error instanceof RangeError)) return false;
  if (!/Maximum call stack size exceeded/.test(error.message)) return false;
  const mechanism = event.exception?.values?.[0]?.mechanism?.type;
  if (mechanism !== 'onerror' && mechanism !== 'onunhandledrejection') return false;
  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
  // No real frames at all → definitely a third-party inline/eval script.
  if (frames.length === 0) return true;
  // Any in-app frame means we want to keep it for investigation.
  return !frames.some(f => f.in_app === true);
};

Sentry.init({
  dsn: useRuntimeConfig().public.sentry.dsn,
  beforeSend(event, hint) {
    const err = hint?.originalException;
    if (isCancelledFetchError(err)) return null;
    if (isHandledChunkLoadFailure(err)) return null;
    if (isThirdPartyStackOverflow(err, event)) return null;
    return event;
  },
});
